import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { CreditCard, Banknote, Wallet, AlertTriangle } from 'lucide-react';
import { reconciliationAPI } from '../api';

/**
 * Liabilities dashboard panel (plan §11, Phase 3).
 *
 * Shows the tenant's current liability-ish exposure at a glance:
 *   - Credit card balances        (asset-inverted; negative means owed)
 *   - Outstanding loan principal
 *   - Proprietor drawings equity
 *   - Business-spend-from-personal owed back to proprietor(s)
 *
 * Data source: GET /api/reconciliation/liabilities. We render nothing
 * when the tenant has no relevant accounts so the dashboard stays
 * clean for new/pure-cash tenants.
 */

const currency = (n) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(n ?? 0));

function Row({ icon: Icon, label, value, hint, tone = 'default' }) {
  const toneClasses = {
    default: 'text-[var(--ds-text-primary)]',
    warn: 'text-amber-700 dark:text-amber-400',
    good: 'text-green-700 dark:text-green-400',
  };
  return (
    <div className="flex items-center justify-between gap-2 py-1.5">
      <div className="flex min-w-0 items-center gap-2">
        <Icon className="h-4 w-4 flex-shrink-0 text-[var(--ds-text-muted)]" />
        <div className="min-w-0">
          <div className="truncate text-xs text-[var(--ds-text-primary)]">
            {label}
          </div>
          {hint ? (
            <div className="truncate text-[10px] text-[var(--ds-text-muted)]">
              {hint}
            </div>
          ) : null}
        </div>
      </div>
      <div className={`text-xs font-medium tabular-nums ${toneClasses[tone]}`}>
        {value}
      </div>
    </div>
  );
}

export default function LiabilitiesPanel() {
  const { data, isLoading, error } = useQuery(
    ['reconciliation', 'liabilities'],
    () => reconciliationAPI.getLiabilities(),
    {
      staleTime: 60_000,
      refetchOnWindowFocus: true,
    }
  );

  if (isLoading) {
    return (
      <div className="bg-[var(--ds-surface-panel)] rounded-[var(--ds-radius-lg)] shadow-[var(--ds-shadow-card)] p-4">
        <div className="text-meta text-[var(--ds-text-muted)]">Loading liabilities…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-[var(--ds-surface-panel)] rounded-[var(--ds-radius-lg)] shadow-[var(--ds-shadow-card)] p-4">
        <div className="flex items-start gap-2 text-xs text-amber-700">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>
            {error.response?.data?.error ||
              error.message ||
              'Failed to load liabilities'}
          </span>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const hasAnything =
    (data.creditCards?.length || 0) +
      (data.loans?.length || 0) +
      (data.drawings?.length || 0) +
      (data.owedToProprietor?.length || 0) >
    0;
  if (!hasAnything) return null;

  return (
    <div className="bg-[var(--ds-surface-panel)] rounded-[var(--ds-radius-lg)] shadow-[var(--ds-shadow-card)]">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--ds-border-divider)]">
        <h3 className="text-h2 text-[var(--ds-text-primary)] font-semibold">
          Liabilities
        </h3>
        <Link
          to="/tenant/banking/reconciliation"
          className="text-[11px] text-blue-600 hover:underline"
        >
          Reconciliation
        </Link>
      </div>
      <div className="p-4 space-y-3">
        {data.creditCards?.length > 0 && (
          <section>
            <div className="mb-1 flex items-center justify-between">
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ds-text-secondary)]">
                Credit cards
              </h4>
              <span className="text-[11px] text-[var(--ds-text-muted)]">
                {currency(data.totals?.creditCards)}
              </span>
            </div>
            <div className="divide-y divide-[var(--ds-border-divider)]">
              {data.creditCards.map((c) => (
                <Row
                  key={c.id}
                  icon={CreditCard}
                  label={c.accountName}
                  hint={c.ownerLabel}
                  value={currency(c.currentBalance)}
                  tone={Number(c.currentBalance) < 0 ? 'warn' : 'default'}
                />
              ))}
            </div>
          </section>
        )}

        {data.loans?.length > 0 && (
          <section>
            <div className="mb-1 flex items-center justify-between">
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ds-text-secondary)]">
                Loans
              </h4>
              <span className="text-[11px] text-[var(--ds-text-muted)]">
                {currency(data.totals?.loans)}
              </span>
            </div>
            <div className="divide-y divide-[var(--ds-border-divider)]">
              {data.loans.map((l) => (
                <Row
                  key={l.id}
                  icon={Banknote}
                  label={l.accountName}
                  hint={l.lenderName || l.ownerLabel}
                  value={currency(l.currentBalance)}
                  tone={Number(l.currentBalance) < 0 ? 'warn' : 'default'}
                />
              ))}
            </div>
          </section>
        )}

        {data.owedToProprietor?.length > 0 && (
          <section>
            <div className="mb-1 flex items-center justify-between">
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ds-text-secondary)]">
                Owed to proprietor
              </h4>
              <span className="text-[11px] text-[var(--ds-text-muted)]">
                {currency(data.totals?.owedToProprietor)}
              </span>
            </div>
            <div className="divide-y divide-[var(--ds-border-divider)]">
              {data.owedToProprietor.map((row) => (
                <Row
                  key={row.drawingsAccountId}
                  icon={Wallet}
                  label={row.accountName}
                  hint={row.ownerLabel}
                  value={currency(row.amount)}
                  tone="warn"
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
